import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, DatePicker, Spin, message, Table, Tag, Alert } from 'antd';
import { DollarOutlined, CheckCircleOutlined, ClockCircleOutlined, WarningOutlined, RiseOutlined, FallOutlined } from '@ant-design/icons';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart } from 'recharts';
import { dashboardService } from '../services/api';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

dayjs.locale('pt-br');

const DashboardFinanceiroDetalhado = () => {
  const [loading, setLoading] = useState(true);
  const [dados, setDados] = useState(null);
  const [mesAno, setMesAno] = useState(dayjs());

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#ffc658', '#ff7c7c'];

  useEffect(() => {
    carregarDados();
  }, [mesAno]);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const params = {
        mes: mesAno.month() + 1,
        ano: mesAno.year()
      };

      const response = await dashboardService.getFinanceiroDetalhado(params);
      setDados(response.data);
    } catch (error) {
      message.error('Erro ao carregar dados do dashboard financeiro');
      console.error(error);
    }
    setLoading(false);
  };

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  if (loading || !dados) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Spin size="large" tip="Carregando dashboard..." />
      </div>
    );
  }

  const { kpis, receitas_por_tipo, despesas_por_categoria, contas_pagar, contas_receber, evolucao_mensal } = dados;

  const colunasContasPagar = [
    {
      title: 'Descrição',
      dataIndex: 'descricao',
      key: 'descricao',
      width: 250,
      ellipsis: true,
    },
    {
      title: 'Categoria',
      dataIndex: 'categoria',
      key: 'categoria',
      width: 120,
      render: (cat) => <Tag color="blue">{cat}</Tag>,
    },
    {
      title: 'Vencimento',
      dataIndex: 'vencimento',
      key: 'vencimento',
      width: 100,
    },
    {
      title: 'Dias',
      dataIndex: 'dias_para_vencer',
      key: 'dias_para_vencer',
      width: 80,
      align: 'center',
      render: (dias) => (
        <Tag color={dias <= 7 ? 'red' : dias <= 15 ? 'orange' : 'green'}>
          {dias}d
        </Tag>
      ),
    },
    {
      title: 'Valor',
      dataIndex: 'valor',
      key: 'valor',
      width: 120,
      align: 'right',
      render: (valor) => formatarMoeda(valor),
    },
  ];

  const colunasContasReceber = [
    {
      title: 'Descrição',
      dataIndex: 'descricao',
      key: 'descricao',
      width: 300,
      ellipsis: true,
    },
    {
      title: 'Vencimento',
      dataIndex: 'vencimento',
      key: 'vencimento',
      width: 100,
    },
    {
      title: 'Dias',
      dataIndex: 'dias_para_vencer',
      key: 'dias_para_vencer',
      width: 80,
      align: 'center',
      render: (dias) => (
        <Tag color={dias <= 7 ? 'red' : dias <= 15 ? 'orange' : 'green'}>
          {dias}d
        </Tag>
      ),
    },
    {
      title: 'Valor',
      dataIndex: 'valor',
      key: 'valor',
      width: 120,
      align: 'right',
      render: (valor) => formatarMoeda(valor),
    },
  ];

  return (
    <div className="dashboard-page" style={{ padding: '24px' }}>
      {/* Cabeçalho */}
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <h1 style={{ margin: 0 }}>Dashboard Financeiro Detalhado</h1>
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Análise completa de receitas e despesas</p>
        </Col>
        <Col>
          <DatePicker
            picker="month"
            value={mesAno}
            onChange={(date) => setMesAno(date || dayjs())}
            format="MMMM/YYYY"
            style={{ width: 200 }}
          />
        </Col>
      </Row>

      {/* Alertas */}
      {(kpis.receitas.vencidas > 0 || kpis.despesas.vencidas > 0) && (
        <Alert
          message="Atenção: Contas Vencidas!"
          description={
            <>
              {kpis.receitas.vencidas > 0 && <div>• Receitas vencidas: {formatarMoeda(kpis.receitas.vencidas)}</div>}
              {kpis.despesas.vencidas > 0 && <div>• Despesas vencidas: {formatarMoeda(kpis.despesas.vencidas)}</div>}
            </>
          }
          type="warning"
          showIcon
          style={{ marginBottom: '24px' }}
        />
      )}

      {/* KPIs Principais */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Receitas Recebidas"
              value={kpis.receitas.recebidas}
              precision={2}
              prefix="R$"
              valueStyle={{ color: '#52c41a' }}
            />
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
              Previstas: {formatarMoeda(kpis.receitas.pendentes)}
            </p>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Inadimplência"
              value={kpis.inadimplencia?.valor_devido || 0}
              precision={2}
              prefix="R$"
              valueStyle={{ color: '#cf1322' }}
            />
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
              {kpis.inadimplencia?.quantidade || 0} títulos em aberto vencidos
            </p>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="Despesas Pagas"
              value={kpis.despesas.pagas}
              precision={2}
              prefix="R$"
              valueStyle={{ color: '#ff4d4f' }}
            />
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
              Previstas: {formatarMoeda(kpis.despesas.pendentes)}
            </p>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={12}>
          <Card>
            <Statistic
              title="Saldo Realizado"
              value={kpis.resultado.saldo_realizado}
              precision={2}
              prefix={kpis.resultado.saldo_realizado >= 0 ? <RiseOutlined /> : <FallOutlined />}
              valueStyle={{ color: kpis.resultado.saldo_realizado >= 0 ? '#52c41a' : '#ff4d4f' }}
            />
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
              Projetado: {formatarMoeda(kpis.resultado.saldo_projetado)}
            </p>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={12}>
          <Card>
            {kpis.resultado.margem_operacional === null ? (
              <Statistic title="Margem Operacional" value="—" />
            ) : (
              <Statistic
                title="Margem Operacional"
                value={kpis.resultado.margem_operacional}
                precision={1}
                suffix="%"
                valueStyle={{ color: kpis.resultado.margem_operacional >= 20 ? '#52c41a' : '#faad14' }}
              />
            )}
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
              Meta: 20%
            </p>
          </Card>
        </Col>
      </Row>

      {/* Indicadores Financeiros */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12}>
          <Card title="Indicadores Financeiros" size="small">
            <Row gutter={16}>
              <Col span={12}>
                <Statistic
                  title="Cobertura de Despesas"
                  value={kpis.indicadores.cobertura_despesas}
                  precision={2}
                  suffix="x"
                  valueStyle={{ fontSize: '20px', color: kpis.indicadores.cobertura_despesas >= 1 ? '#52c41a' : '#ff4d4f' }}
                />
                <p style={{ margin: '8px 0 0', fontSize: '11px', color: 'var(--text-secondary)' }}>
                  {kpis.indicadores.cobertura_despesas >= 1 ? '✅ Positivo' : '⚠️ Insuficiente'}
                </p>
              </Col>
            </Row>
          </Card>
        </Col>

        <Col xs={24} sm={12}>
          <Card title="Receitas por Origem" size="small">
            <ResponsiveContainer width="100%" height={120}>
              <BarChart data={receitas_por_tipo} layout="vertical">
                <XAxis type="number" />
                <YAxis dataKey="tipo" type="category" width={80} />
                <Tooltip formatter={(value) => formatarMoeda(value)} />
                <Bar dataKey="recebido" stackId="a" fill="#52c41a" name="Recebido" />
                <Bar dataKey="previsto" stackId="a" fill="#faad14" name="Previsto" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Gráficos */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={16}>
          <Card title="Evolução Mensal (6 meses)">
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={evolucao_mensal}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip formatter={(value, name) => {
                  if (name === 'Margem %') return `${value}%`;
                  return formatarMoeda(value);
                }} />
                <Legend />
                <Bar yAxisId="left" dataKey="receitas" fill="#52c41a" name="Receitas" />
                <Bar yAxisId="left" dataKey="despesas" fill="#ff4d4f" name="Despesas" />
                <Line yAxisId="right" type="monotone" dataKey="margem" stroke="#1890ff" strokeWidth={2} name="Margem %" />
              </ComposedChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Despesas por Categoria">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={despesas_por_categoria}
                  dataKey="valor"
                  nameKey="categoria"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={(entry) => `${entry.percentual}%`}
                >
                  {despesas_por_categoria.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatarMoeda(value)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Tabelas de Contas */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Contas a Pagar (30 dias)" extra={<WarningOutlined style={{ color: '#ff4d4f' }} />}>
            <Table
              dataSource={contas_pagar}
              columns={colunasContasPagar}
              pagination={false}
              size="small"
              rowKey={(record, index) => index}
              scroll={{ y: 300 }}
              summary={(data) => {
                const total = data.reduce((sum, item) => sum + item.valor, 0);
                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={4} align="right">
                        <strong>Total:</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={4} align="right">
                        <strong>{formatarMoeda(total)}</strong>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Contas a Receber (30 dias)" extra={<CheckCircleOutlined style={{ color: '#52c41a' }} />}>
            <Table
              dataSource={contas_receber}
              columns={colunasContasReceber}
              pagination={false}
              size="small"
              rowKey={(record, index) => index}
              scroll={{ y: 300 }}
              summary={(data) => {
                const total = data.reduce((sum, item) => sum + item.valor, 0);
                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={3} align="right">
                        <strong>Total:</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="right">
                        <strong>{formatarMoeda(total)}</strong>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DashboardFinanceiroDetalhado;
